// Modal grid of templates shown when the user hits "New board". Picking a
// card writes a pending-template marker for the new board id and navigates
// to /b/{id}; useBoardDoc consumes the marker on first paint and seeds.

import { useCallback, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { X } from 'lucide-react';
import type { Template } from '@frame/shared';
import { listTemplates, deleteUserTemplate } from '../registry';
import { setPendingTemplate } from '../pendingTemplate';
import { TemplateThumbnail } from './TemplateThumbnail';
import { Button } from '../../components/Button';

interface Props {
  onClose: () => void;
  // Generator for the new board's id, injectable so tests / dev sandboxes
  // can pin a deterministic id.
  newBoardId: () => string;
  // Refresh-the-list callback so saving/deleting user templates re-renders
  // the picker. The picker itself doesn't subscribe to storage — it just
  // re-reads on each render and bumps a parent counter when needed.
  onRefresh?: () => void;
}

export function TemplatePicker({ onClose, newBoardId, onRefresh }: Props) {
  const navigate = useNavigate();
  const templates = useMemo(() => listTemplates(), []);

  const pick = useCallback(
    (template: Template) => {
      const id = newBoardId();
      setPendingTemplate(id, template.id);
      navigate(`/b/${id}`);
    },
    [navigate, newBoardId],
  );

  // Esc closes.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Choose a template"
      className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-8"
      onClick={onClose}
    >
      <div
        className="
          relative w-full max-w-[920px] max-h-[80vh]
          bg-bg-panel border border-border-default rounded-3 shadow-md
          flex flex-col
        "
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-center justify-between px-6 py-4 border-b border-border-subtle">
          <div>
            <h2 className="text-16 font-semibold text-fg-primary m-0">New board</h2>
            <p className="text-12 text-fg-tertiary m-0 mt-1">
              Pick a template — your board opens with its shapes already laid out.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="
              w-8 h-8 inline-flex items-center justify-center rounded-2
              text-fg-tertiary hover:bg-bg-hover hover:text-fg-primary
              transition-colors duration-micro ease-out
              focus-visible:outline-none focus-visible:shadow-focus
            "
          >
            <X size={16} strokeWidth={1.75} />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto p-6">
          <div className="grid grid-cols-[repeat(auto-fill,minmax(220px,1fr))] gap-4">
            {templates.map((template) => (
              <TemplateCard
                key={template.id}
                template={template}
                onPick={() => pick(template)}
                onDelete={
                  template.kind === 'user'
                    ? () => {
                        deleteUserTemplate(template.id);
                        onRefresh?.();
                      }
                    : undefined
                }
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function TemplateCard({
  template,
  onPick,
  onDelete,
}: {
  template: Template;
  onPick: () => void;
  onDelete?: () => void;
}) {
  return (
    <div className="relative group">
      <button
        type="button"
        onClick={onPick}
        className="
          w-full text-left
          bg-bg-app border border-border-default rounded-3 overflow-hidden
          hover:border-accent transition-colors duration-micro ease-out
          focus-visible:outline-none focus-visible:shadow-focus
        "
      >
        <div className="aspect-[5/3] bg-bg-canvas border-b border-border-subtle">
          <TemplateThumbnail template={template} width={220} height={132} />
        </div>
        <div className="px-3 py-2.5">
          <div className="flex items-center gap-2">
            <span className="text-13 font-semibold text-fg-primary">{template.name}</span>
            {template.kind === 'user' && (
              <span className="text-10 uppercase tracking-wide text-fg-tertiary font-medium">
                yours
              </span>
            )}
          </div>
          <p
            className="m-0 mt-0.5 text-12 text-fg-secondary leading-[1.35] overflow-hidden"
            style={{
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
            }}
          >
            {template.description}
          </p>
        </div>
      </button>
      {onDelete && (
        <button
          type="button"
          onClick={onDelete}
          aria-label={`Delete ${template.name}`}
          title={`Delete ${template.name}`}
          className="
            absolute top-2 right-2 w-7 h-7 inline-flex items-center justify-center rounded-2
            bg-bg-panel border border-border-default text-fg-tertiary
            opacity-0 group-hover:opacity-100 hover:bg-error-subtle hover:text-error
            transition-opacity duration-micro ease-out
            focus-visible:outline-none focus-visible:shadow-focus focus-visible:opacity-100
          "
        >
          <X size={12} strokeWidth={1.75} />
        </button>
      )}
    </div>
  );
}

// Convenience hook for the "New board" button — opens the picker with a
// freshly-generated board id factory. Exported so callers don't have to
// duplicate the id generator everywhere.
export function makeBoardId(): string {
  const alphabet = 'abcdefghijklmnopqrstuvwxyz0123456789';
  let out = '';
  for (let i = 0; i < 6; i++) out += alphabet[Math.floor(Math.random() * alphabet.length)];
  return out;
}

// Re-export Button so the picker's footer can grow CTAs without callers
// importing from another file.
export { Button };
